import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { User, Mail, Phone, Calendar, Package, DollarSign, Search, ArrowUpDown, Filter, Users as UsersIcon } from 'lucide-react';

interface UserPackage {
  id: string;
  name: string;
  version: string;
  price: number;
  purchaseDate: any;
}

interface CoursedeeUser {
  id: string;
  email: string;
  fullName: string;
  phone: string;
  createdAt: any;
  coursedeePackages: UserPackage[];
  totalSpent: number;
  packageCount: number;
}

type SortBy = 'date-desc' | 'date-asc' | 'spent-desc' | 'spent-asc';
type TimeFilter = 'all' | 'today' | '7days' | '30days' | 'thisMonth';

export default function CoursedeeUsersAdmin() {
  const [users, setUsers] = useState<CoursedeeUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SortBy>('date-desc');
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all');
  const [packageFilter, setPackageFilter] = useState<string>('all');
  const [availablePackages, setAvailablePackages] = useState<string[]>([]);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);

      // Load all users with coursedeePackages
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);

      // Load all payments to calculate total spent
      const paymentsRef = collection(db, 'subscription_payments');
      const paymentsQuery = query(
        paymentsRef,
        where('source', '==', 'coursedee_cart'),
        where('status', '==', 'verified')
      );
      const paymentsSnapshot = await getDocs(paymentsQuery);

      // Create payment map by userId
      const paymentMap = new Map<string, number>();
      paymentsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const userId = data.userId;
        const amount = data.amount || 0;
        paymentMap.set(userId, (paymentMap.get(userId) || 0) + amount);
      });

      // Filter users with coursedeePackages
      const coursedeeUsers: CoursedeeUser[] = [];
      const packagesSet = new Set<string>();

      usersSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.coursedeePackages && Array.isArray(data.coursedeePackages) && data.coursedeePackages.length > 0) {
          const totalSpent = paymentMap.get(doc.id) || 0;
          
          coursedeeUsers.push({
            id: doc.id,
            email: data.email || '',
            fullName: data.fullName || data.displayName || 'ไม่ระบุชื่อ',
            phone: data.phone || '',
            createdAt: data.createdAt,
            coursedeePackages: data.coursedeePackages,
            totalSpent: totalSpent,
            packageCount: data.coursedeePackages.length
          });

          // Collect unique packages
          data.coursedeePackages.forEach((pkg: any) => {
            packagesSet.add(pkg.name);
          });
        }
      });

      setUsers(coursedeeUsers);
      setAvailablePackages(Array.from(packagesSet).sort());
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredAndSortedUsers = () => {
    let filtered = [...users];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        user.fullName.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term) ||
        user.phone.includes(term)
      );
    }

    // Time filter
    if (timeFilter !== 'all') {
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

      filtered = filtered.filter(user => {
        if (!user.createdAt) return false;
        const createdDate = user.createdAt.toDate();

        switch (timeFilter) {
          case 'today':
            return createdDate >= todayStart;
          case '7days':
            const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return createdDate >= sevenDaysAgo;
          case '30days':
            const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            return createdDate >= thirtyDaysAgo;
          case 'thisMonth':
            return createdDate >= monthStart;
          default:
            return true;
        }
      });
    }

    // Package filter
    if (packageFilter !== 'all') {
      filtered = filtered.filter(user =>
        user.coursedeePackages.some(pkg => pkg.name === packageFilter)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0);
        case 'date-asc':
          return (a.createdAt?.toMillis() || 0) - (b.createdAt?.toMillis() || 0);
        case 'spent-desc':
          return b.totalSpent - a.totalSpent;
        case 'spent-asc':
          return a.totalSpent - b.totalSpent;
        default:
          return 0;
      }
    });

    return filtered;
  };

  const filteredUsers = getFilteredAndSortedUsers();

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '-';
    return timestamp.toDate().toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0
    }).format(price);
  };

  // Calculate stats
  const totalUsers = filteredUsers.length;
  const totalRevenue = filteredUsers.reduce((sum, user) => sum + user.totalSpent, 0);
  
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  
  const newUsersToday = users.filter(user => 
    user.createdAt && user.createdAt.toDate() >= todayStart
  ).length;
  
  const newUsersThisMonth = users.filter(user => 
    user.createdAt && user.createdAt.toDate() >= monthStart
  ).length;

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">กำลังโหลดข้อมูล...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">ผู้ใช้ทั้งหมด</p>
              <p className="text-3xl font-bold mt-1">{totalUsers}</p>
            </div>
            <UsersIcon className="w-12 h-12 text-purple-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">ผู้ใช้ใหม่เดือนนี้</p>
              <p className="text-3xl font-bold mt-1">{newUsersThisMonth}</p>
            </div>
            <Calendar className="w-12 h-12 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">ผู้ใช้ใหม่วันนี้</p>
              <p className="text-3xl font-bold mt-1">{newUsersToday}</p>
            </div>
            <User className="w-12 h-12 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium">รายได้จากผู้ใช้</p>
              <p className="text-2xl font-bold mt-1">{formatPrice(totalRevenue)}</p>
            </div>
            <DollarSign className="w-12 h-12 text-orange-200" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="ค้นหาด้วยชื่อ, อีเมล, หรือเบอร์โทร..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Filters Row */}
          <div className="flex flex-col md:flex-row gap-3">
            {/* Sort */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <ArrowUpDown className="w-4 h-4 inline mr-1" />
                เรียงลำดับ
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortBy)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="date-desc">วันสมัคร: ล่าสุด → เก่าสุด</option>
                <option value="date-asc">วันสมัคร: เก่าสุด → ล่าสุด</option>
                <option value="spent-desc">ยอดชำระ: มากสุด → น้อยสุด</option>
                <option value="spent-asc">ยอดชำระ: น้อยสุด → มากสุด</option>
              </select>
            </div>

            {/* Time Filter */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Calendar className="w-4 h-4 inline mr-1" />
                ช่วงเวลา
              </label>
              <select
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value as TimeFilter)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="all">ทั้งหมด</option>
                <option value="today">วันนี้</option>
                <option value="7days">7 วันล่าสุด</option>
                <option value="30days">30 วันล่าสุด</option>
                <option value="thisMonth">เดือนนี้</option>
              </select>
            </div>

            {/* Package Filter */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Filter className="w-4 h-4 inline mr-1" />
                แพ็คเก็จ
              </label>
              <select
                value={packageFilter}
                onChange={(e) => setPackageFilter(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="all">ทั้งหมด</option>
                {availablePackages.map(pkg => (
                  <option key={pkg} value={pkg}>{pkg}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Users List */}
      <div className="space-y-4">
        {filteredUsers.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <UsersIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">ไม่พบผู้ใช้งาน</h3>
            <p className="text-gray-600">ลองเปลี่ยนตัวกรองหรือคำค้นหา</p>
          </div>
        ) : (
          filteredUsers.map((user) => (
            <div
              key={user.id}
              className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                {/* Left: User Info */}
                <div className="flex-1 space-y-3">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <User className="w-5 h-5 text-purple-600" />
                      <h3 className="text-lg font-bold text-gray-900">{user.fullName}</h3>
                    </div>
                    <div className="flex flex-col gap-1.5 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        {user.email}
                      </div>
                      {user.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          {user.phone}
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        สมัครเมื่อ: {formatDate(user.createdAt)}
                      </div>
                    </div>
                  </div>

                  {/* Packages */}
                  <div className="bg-purple-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Package className="w-4 h-4 text-purple-600" />
                      <p className="text-sm font-semibold text-gray-700">
                        แพ็คเก็จที่ซื้อ ({user.packageCount})
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      {user.coursedeePackages.map((pkg, index) => (
                        <div key={index} className="flex items-center justify-between text-sm">
                          <span className="text-gray-900 font-medium">
                            {pkg.name} - {pkg.version}
                          </span>
                          <span className="text-gray-600">
                            {formatPrice(pkg.price)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right: Stats */}
                <div className="lg:w-48">
                  <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-4 text-center">
                    <p className="text-sm text-gray-600 mb-1">ยอดชำระรวม</p>
                    <p className="text-3xl font-bold text-orange-600">
                      {formatPrice(user.totalSpent)}
                    </p>
                    <p className="text-xs text-gray-600 mt-2">
                      {user.packageCount} แพ็คเก็จ
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Results Count */}
      {filteredUsers.length > 0 && (
        <div className="text-center text-sm text-gray-600">
          แสดง {filteredUsers.length} จาก {users.length} ผู้ใช้
        </div>
      )}
    </div>
  );
}
